import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { login, try_for_free } from "../../url";
import logo from "../../Assets/logo.png";
import LanguageSwitcher from "../common/LanguageSwitcher";

const NAV_CONFIG = [
  { key: "home", defaultText: "Home", path: "/" },
  { key: "about", defaultText: "About", path: "/about" },
  { key: "features", defaultText: "Features", path: "/features" },
  { key: "security", defaultText: "Security & Compliance", path: "/security" },
  { key: "pricing", defaultText: "Pricing Plans", path: "/pricing" },
  { key: "partners", defaultText: "Partners", path: "/partners" },
  { key: "contact", defaultText: "Contact", path: "/contact" },
];

const isRef2026Route = (path) => path.startsWith("/");

function Navbar() {
  const { t } = useTranslation(['navbar', 'common']);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap');
        :root {
          --rs-ink: #163A5A;
          --rs-ink2: #3D6A8C;
          --rs-ink3: #5E87A3;
          --rs-sky: #1A63A5;
          --rs-mint: #2AB6E8;
          --rs-secondary: #2AB6E8;
          --rs-secondary-deep: #1AA3D4;
          --rs-secondary-soft: #D9F4FC;
          --rs-secondary-muted: rgba(42,182,232,0.18);
          --rs-sky-light: #E8F4FC;
          --rs-sky-hover: #155a94;
          --rs-surface: #F5F9FC;
          --rs-ff-head: 'Plus Jakarta Sans', system-ui, sans-serif;
          --rs-ff-body: 'Plus Jakarta Sans', system-ui, sans-serif;
        }
        .rs-navbar {
          position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
          height: 72px; background: rgba(255,255,255,0.98);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border-bottom: 1px solid rgba(26,99,165,0.12);
          transition: box-shadow 0.2s;
          display: flex; align-items: center;
        }
        .rs-navbar.scrolled { box-shadow: 0 4px 24px rgba(10,15,30,0.07); border-bottom-color: rgba(42,182,232,0.25); }
        .rs-navbar-inner {
          max-width: 1320px; width: 100%; margin: 0 auto;
          padding: 0 20px; display: flex; align-items: center;
          justify-content: space-between;
          box-sizing: border-box;
          gap: 12px;
        }
        .rs-nav-logo {
          font-family: var(--rs-ff-head); font-weight: 800; font-size: 20px;
          color: var(--rs-ink); text-decoration: none;
          display: flex; align-items: center;
          flex-shrink: 0;
        }
        .rs-nav-logo img { height: 32px; width: auto; display: block; }
        .rs-nav-links {
          display: flex; align-items: center; gap: 4px;
          list-style: none; margin: 0; padding: 0;
          flex-shrink: 1;
        }
        .rs-nav-links a {
          font-family: var(--rs-ff-body); font-size: 13px; color: var(--rs-ink2);
          text-decoration: none; font-weight: 500; padding: 7px 11px;
          border-radius: 8px; transition: all 0.2s; letter-spacing: 0.01em;
          border: 1px solid rgba(26,99,165,0.22);
          background: linear-gradient(180deg, #ffffff 0%, var(--rs-surface) 100%);
          display: inline-flex;
          align-items: center;
          line-height: 1.2;
          box-sizing: border-box;
          box-shadow: 0 1px 2px rgba(22,58,90,0.06);
          white-space: nowrap;
        }
        .rs-nav-links a:hover {
          color: var(--rs-sky);
          background: linear-gradient(180deg, var(--rs-sky-light) 0%, var(--rs-secondary-soft) 100%);
          border-color: var(--rs-secondary);
        }
        .rs-nav-links a.active {
          color: var(--rs-sky);
          font-weight: 600;
          border-color: var(--rs-secondary);
          background: linear-gradient(135deg, var(--rs-sky-light) 0%, var(--rs-secondary-soft) 100%);
          box-shadow: 0 0 0 1px rgba(42,182,232,0.22), 0 4px 12px rgba(42,182,232,0.12);
        }
        .rs-nav-links button.rs-nav-disabled {
          appearance: none;
          -webkit-appearance: none;
          font-family: var(--rs-ff-body);
          font-size: 13px;
          font-weight: 500;
          color: var(--rs-ink3);
          padding: 7px 11px;
          border-radius: 8px;
          border: 1px solid rgba(26,99,165,0.22);
          background: linear-gradient(180deg, #ffffff 0%, var(--rs-surface) 100%);
          cursor: not-allowed;
          letter-spacing: 0.01em;
          line-height: 1.2;
          margin: 0;
          box-shadow: 0 1px 2px rgba(22,58,90,0.06);
          white-space: nowrap;
        }
        .rs-nav-links button.rs-nav-disabled:disabled {
          opacity: 1;
        }
        .rs-nav-actions {
          display: flex; gap: 8px; align-items: center;
          flex-shrink: 0;
          white-space: nowrap;
        }
        .rs-nav-btn {
          font-family: var(--rs-ff-body); font-size: 13px; font-weight: 600;
          padding: 7px 15px; border-radius: 8px; cursor: pointer;
          transition: all 0.2s; letter-spacing: 0.01em; text-decoration: none;
          display: inline-flex; align-items: center; justify-content: center;
          box-sizing: border-box;
          border: 1px solid rgba(26,99,165,0.22);
          white-space: nowrap;
          line-height: 1.3;
        }
        .rs-nav-btn-ghost {
          background: var(--rs-secondary);
          border: 1px solid var(--rs-secondary);
          color: #fff;
        }
        .rs-nav-btn-ghost:hover { background: var(--rs-secondary-deep); border-color: var(--rs-secondary-deep); color: #fff; }
        .rs-nav-btn-primary { background: var(--rs-sky); border: 1px solid var(--rs-sky); color: #fff; box-shadow: 0 2px 8px rgba(42,182,232,0.35); }
        .rs-nav-btn-primary:hover { background: var(--rs-sky-hover); border-color: var(--rs-sky-hover); transform: translateY(-1px); box-shadow: 0 4px 14px rgba(42,182,232,0.4); }

        .rs-nav-hamburger {
          display: none;
          background: #fff;
          border: 1px solid rgba(26,99,165,0.22);
          border-radius: 8px;
          cursor: pointer;
          color: var(--rs-ink);
          padding: 8px;
          box-sizing: border-box;
          z-index: 1100;
          align-items: center;
          justify-content: center;
        }
        .rs-nav-hamburger svg { width: 22px; height: 22px; transition: transform .3s; }
        .rs-nav-hamburger.open svg { transform: rotate(90deg); }

        /* Mobile Drawer */
        .rs-mobile-drawer {
          display: none; position: fixed; inset: 0; z-index: 999;
        }
        .rs-mobile-drawer.open { display: block; }
        .rs-mobile-overlay {
          position: absolute; inset: 0; background: rgba(10,15,30,0.45);
          backdrop-filter: blur(3px);
        }
        .rs-mobile-panel {
          position: absolute; top: 0; left: 0; right: 0;
          background: #fff; padding: 20px 24px 32px;
          border-bottom-left-radius: 20px; border-bottom-right-radius: 20px;
          box-shadow: 0 16px 48px rgba(10,15,30,0.15);
          max-height: 90vh;
          overflow-y: auto;
        }
        .rs-mobile-header {
          display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;
        }
        .rs-mobile-nav { list-style: none; margin: 0 0 20px; padding: 0; }
        .rs-mobile-nav li { margin-bottom: 8px; }
        .rs-mobile-nav li:last-child { margin-bottom: 0; }
        .rs-mobile-nav li a {
          display: block; font-family: var(--rs-ff-body); font-size: 15px;
          color: var(--rs-ink2); text-decoration: none; font-weight: 500;
          padding: 10px 16px; border-radius: 10px;
          transition: color 0.2s, background 0.2s, border-color 0.2s;
          border: 1px solid rgba(26,99,165,0.22);
          background: linear-gradient(180deg, #ffffff 0%, var(--rs-surface) 100%);
          box-sizing: border-box;
          text-align: center;
          box-shadow: 0 1px 2px rgba(22,58,90,0.06);
          white-space: nowrap;
        }
        .rs-mobile-nav li a:hover, .rs-mobile-nav li a.active {
          color: var(--rs-sky);
          background: linear-gradient(135deg, var(--rs-sky-light) 0%, var(--rs-secondary-soft) 100%);
          border-color: var(--rs-secondary);
          box-shadow: 0 0 0 1px rgba(42,182,232,0.15);
        }
        .rs-mobile-nav li button.rs-nav-disabled {
          appearance: none;
          -webkit-appearance: none;
          display: block;
          width: 100%;
          box-sizing: border-box;
          font-family: var(--rs-ff-body);
          font-size: 14px;
          font-weight: 500;
          color: var(--rs-ink3);
          padding: 10px 16px;
          border-radius: 10px;
          border: 1px solid rgba(26,99,165,0.22);
          background: var(--rs-surface);
          cursor: not-allowed;
          text-align: center;
          line-height: 1.3;
          box-shadow: 0 1px 2px rgba(22,58,90,0.06);
          white-space: nowrap;
        }
        .rs-mobile-nav li button.rs-nav-disabled:disabled {
          opacity: 1;
        }
        .rs-mobile-actions {
          display: flex; flex-direction: column; gap: 10px;
          padding-top: 14px;
          border-top: 1px solid rgba(26,99,165,0.1);
        }
        .rs-mobile-actions .rs-nav-btn {
          width: 100%; justify-content: center; padding: 11px 20px; font-size: 14px;
        }

        /* Responsive Breakpoints for Multilingual Navigation */
        @media (min-width: 1161px) and (max-width: 1280px) {
          .rs-navbar-inner { padding: 0 14px; gap: 8px; }
          .rs-nav-links { gap: 3px; }
          .rs-nav-links a { padding: 6px 8px; font-size: 12.5px; }
          .rs-nav-actions { gap: 6px; }
          .rs-nav-btn { padding: 6px 12px; font-size: 12.5px; }
        }

        @media (max-width: 1160px) {
          .rs-nav-links, .rs-nav-actions { display: none !important; }
          .rs-nav-hamburger { display: flex !important; }
          .rs-navbar-inner { padding: 0 18px; }
        }
      `}</style>

      <nav className={`rs-navbar${scrolled ? " scrolled" : ""}`}>
        <div className="rs-navbar-inner">
          {/* Logo */}
          <NavLink to="/" end className="rs-nav-logo">
            <img src={logo} alt="RequireSign" />
          </NavLink>

          {/* Desktop Links */}
          <ul className="rs-nav-links">
            {NAV_CONFIG.map(({ key, defaultText, path }) => (
              <li key={path}>
                {isRef2026Route(path) ? (
                  <NavLink
                    to={path}
                    end
                    className={({ isActive }) => (isActive ? "active" : "")}
                  >
                    {t(key, defaultText)}
                  </NavLink>
                ) : (
                  <button
                    type="button"
                    className="rs-nav-disabled"
                    disabled
                    title="Main site only - use homepage menu"
                  >
                    {t(key, defaultText)}
                  </button>
                )}
              </li>
            ))}
          </ul>

          {/* Desktop Actions */}
          <div className="rs-nav-actions">
            <LanguageSwitcher />
            <a
              href={login}
              target="_blank"
              rel="noreferrer"
              className="rs-nav-btn rs-nav-btn-ghost"
            >
              {t('navbar:login', 'Login')}
            </a>
            <a
              href={try_for_free}
              target="_blank"
              rel="noreferrer"
              className="rs-nav-btn rs-nav-btn-primary"
            >
              {t('common:buttons.freeTrial', 'Try for FREE')}
            </a>
          </div>

          {/* Mobile Toggle Button */}
          <button
            className={`rs-nav-hamburger${mobileOpen ? " open" : ""}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Drawer */}
      <div className={`rs-mobile-drawer${mobileOpen ? " open" : ""}`}>
        <div
          className="rs-mobile-overlay"
          onClick={() => setMobileOpen(false)}
        />
        <div className="rs-mobile-panel">
          <div className="rs-mobile-header">
            <NavLink
              to="/"
              end
              className="rs-nav-logo"
              onClick={() => setMobileOpen(false)}
            >
              <img src={logo} alt="RequireSign" />
            </NavLink>
          </div>
          <ul className="rs-mobile-nav">
            {NAV_CONFIG.map(({ key, defaultText, path }) => (
              <li key={path}>
                {isRef2026Route(path) ? (
                  <NavLink
                    to={path}
                    end
                    className={({ isActive }) => (isActive ? "active" : "")}
                    onClick={() => setMobileOpen(false)}
                  >
                    {t(key, defaultText)}
                  </NavLink>
                ) : (
                  <button
                    type="button"
                    className="rs-nav-disabled"
                    disabled
                    title="Main site only - use homepage menu"
                  >
                    {t(key, defaultText)}
                  </button>
                )}
              </li>
            ))}
          </ul>
          <div className="rs-mobile-actions">
            <LanguageSwitcher fullWidth={true} />
            <a
              href={login}
              target="_blank"
              rel="noreferrer"
              className="rs-nav-btn rs-nav-btn-ghost"
            >
              {t('navbar:login', 'Login')}
            </a>
            <a
              href={try_for_free}
              target="_blank"
              rel="noreferrer"
              className="rs-nav-btn rs-nav-btn-primary"
            >
              {t('common:buttons.freeTrial', 'Try for FREE')}
            </a>
          </div>
        </div>
      </div>

      {/* Spacer so content isn't hidden under fixed nav */}
      <div style={{ height: 72 }} />
    </>
  );
}

export default Navbar;
